import { Suspense, lazy } from 'react';
import { Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from '@/components/i18n/TranslationProvider';
import { usePublication } from '@/context/PublicationContext.jsx';
import {
  PUBLICATION_ROUTE_OUTCOMES,
  resolvePublicationRouteOutcome,
} from '@/lib/publicationRoutePolicy.js';

const LevelLockedPage = lazy(() => import('@/components/public/LevelLockedPage.jsx'));
const GameOverPage = lazy(() => import('@/components/public/GameOverPage.jsx'));

const FullScreenLoader = () => {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-200">
      <div className="flex items-center gap-3 text-lg font-medium">
        <Loader2 className="h-5 w-5 animate-spin text-indigo-400" />
        <span>{t('routing.loading')}</span>
      </div>
    </div>
  );
};

function PublicationGate({ children }) {
  const location = useLocation();
  const {
    effectivePublicationMode,
    isLoading,
    isPrivilegedUser,
    classifyRoute,
  } = usePublication();

  const currentPath = location.pathname || '/';
  const route = classifyRoute(currentPath);
  const outcome = resolvePublicationRouteOutcome({
    publicationMode: effectivePublicationMode,
    routeClass: route.routeClass,
    isPrivilegedUser,
    isProduction: Boolean(import.meta.env?.PROD),
  });

  if (isLoading) {
    return outcome === PUBLICATION_ROUTE_OUTCOMES.CONTENT
      ? children
      : <FullScreenLoader />;
  }

  if (outcome === PUBLICATION_ROUTE_OUTCOMES.LEVEL_LOCKED) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <LevelLockedPage />
      </Suspense>
    );
  }

  if (outcome === PUBLICATION_ROUTE_OUTCOMES.GAME_OVER) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <GameOverPage />
      </Suspense>
    );
  }

  return children;
}

export default PublicationGate;
